import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { toast } from '../../components/ui/Toast';
import {
  addCharge,
  allocatePaymentCredit,
  approveCharge,
  verifyPaymentClaim,
  rejectPaymentClaim,
  approvePayment,
  getOpenPaymentItems,
  getPayment,
  getPaymentQueue,
  getTransactions,
  markPaymentPaid,
  recordReceipt,
  rejectCharge,
  rejectPayment,
  requestPayout,
  voidCharge,
  voidPayment,
  voidPaymentAllocation,
  absorbCharge,
  addChargeToNextInvoice,
  listPaidNotBilled,
  rejectApproval,
} from '../../api/endpoints/payments';
import { getApprovalLimits, listMyApprovals, putApprovalLimits } from '../../api/endpoints/admin';
import type {
  AddChargeRequest,
  ApproveChargeRequest,
  VerifyClaimRequest,
  MarkPaidRequest,
  PaymentAllocationLine,
  PaymentDirection,
  PaymentTransactionFilters,
  PayoutRequest,
  RecordReceiptRequest,
  AbsorbRequest,
  ApprovalLimits,
} from '../../api/types';

export const paymentKeys = {
  all: ['payments'] as const,
  queue: (mine: boolean) => ['payments', 'queue', { mine }] as const,
  openItems: (direction: PaymentDirection, counterpartyId: number) =>
    ['payments', 'open-items', direction, counterpartyId] as const,
  detail: (paymentId: number) => ['payments', 'detail', paymentId] as const,
  transactions: (filters: PaymentTransactionFilters) =>
    ['payments', 'transactions', filters] as const,
  paidNotBilled: (includeAbsorbed: boolean) =>
    ['payments', 'paid-not-billed', { includeAbsorbed }] as const,
  myApprovals: ['payments', 'approvals', 'mine'] as const,
  approvalLimits: ['payments', 'approval-limits'] as const,
};

function usePaymentMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  { successMessage, ...options }: { retry?: false; successMessage?: () => string } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    ...options,
    onSuccess: async () => {
      // Shared result feedback only (#325): no amount, type or request change.
      if (successMessage) toast.success(successMessage());
      await queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    },
  });
}

/** `successToast: false` for a caller that shows its own, richer result (My approvals). */
interface FeedbackOptions {
  successToast?: boolean;
}

const feedback = (
  key:
    | 'payments.toasts.approved'
    | 'payments.toasts.markedPaid'
    | 'payments.toasts.payoutRequested'
    | 'payments.toasts.receiptRecorded'
    | 'payments.toasts.rejected',
  { successToast = true }: FeedbackOptions = {},
) => (successToast ? () => i18next.t(key) : undefined);

export function usePaymentQueue(mine: boolean) {
  return useQuery({
    queryKey: paymentKeys.queue(mine),
    queryFn: () => getPaymentQueue(mine),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useOpenPaymentItems(direction: PaymentDirection, counterpartyId?: number) {
  return useQuery({
    queryKey: paymentKeys.openItems(direction, counterpartyId ?? 0),
    queryFn: () => getOpenPaymentItems(direction, counterpartyId!),
    enabled: counterpartyId != null,
  });
}

export function usePayment(paymentId?: number) {
  return useQuery({
    queryKey: paymentKeys.detail(paymentId ?? 0),
    queryFn: () => getPayment(paymentId!),
    enabled: paymentId != null,
    refetchInterval: paymentId != null ? 30_000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function usePaymentTransactions(filters: PaymentTransactionFilters) {
  return useQuery({
    queryKey: paymentKeys.transactions(filters),
    queryFn: () => getTransactions(filters),
    enabled: Boolean(filters.from && filters.to),
  });
}

export function useRecordReceipt() {
  return usePaymentMutation(
    ({ request }: { request: RecordReceiptRequest }) => recordReceipt(request),
    { successMessage: feedback('payments.toasts.receiptRecorded') },
  );
}

export function useRequestPayout() {
  return usePaymentMutation(({ request }: { request: PayoutRequest }) => requestPayout(request), {
    successMessage: feedback('payments.toasts.payoutRequested'),
  });
}

export function useAllocatePaymentCredit() {
  return usePaymentMutation(
    ({ paymentId, allocations }: { paymentId: number; allocations: PaymentAllocationLine[] }) =>
      allocatePaymentCredit(paymentId, allocations),
    { retry: false },
  );
}

export function useApprovePayment(options?: FeedbackOptions) {
  return usePaymentMutation((paymentId: number) => approvePayment(paymentId), {
    successMessage: feedback('payments.toasts.approved', options),
  });
}

export function useRejectPayment() {
  return usePaymentMutation(
    ({ paymentId, reason }: { paymentId: number; reason: string }) =>
      rejectPayment(paymentId, reason),
    { successMessage: feedback('payments.toasts.rejected') },
  );
}

export function useMarkPaymentPaid() {
  return usePaymentMutation(
    ({ paymentId, request }: { paymentId: number; request: MarkPaidRequest }) =>
      markPaymentPaid(paymentId, request),
    { retry: false, successMessage: feedback('payments.toasts.markedPaid') },
  );
}

export function useVoidPayment() {
  return usePaymentMutation(({ paymentId, reason }: { paymentId: number; reason: string }) =>
    voidPayment(paymentId, reason),
  );
}

export function useVoidPaymentAllocation() {
  return usePaymentMutation(({ allocationId, reason }: { allocationId: number; reason: string }) =>
    voidPaymentAllocation(allocationId, reason),
  );
}

export function useAddCharge() {
  return usePaymentMutation(
    ({ shipmentId, request }: { shipmentId: number; request: AddChargeRequest }) =>
      addCharge(shipmentId, request),
  );
}

export function useApproveCharge() {
  return usePaymentMutation(
    (input: number | { chargeId: number; request: ApproveChargeRequest }) =>
      typeof input === 'number'
        ? approveCharge(input)
        : approveCharge(input.chargeId, input.request),
  );
}

export function useRejectCharge() {
  return usePaymentMutation(({ chargeId, reason }: { chargeId: number; reason: string }) =>
    rejectCharge(chargeId, reason),
  );
}

export function useVoidCharge() {
  return usePaymentMutation(({ chargeId, reason }: { chargeId: number; reason: string }) =>
    voidCharge(chargeId, reason),
  );
}

export function useRefreshPayments() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: paymentKeys.all });
}

export function useVerifyPaymentClaim() {
  return usePaymentMutation(
    ({ paymentId, request }: { paymentId: number; request: VerifyClaimRequest }) =>
      verifyPaymentClaim(paymentId, request),
  );
}
export function useRejectPaymentClaim() {
  return usePaymentMutation(({ paymentId, reason }: { paymentId: number; reason: string }) =>
    rejectPaymentClaim(paymentId, reason),
  );
}

// ─── Paid, not billed (#293) ─────────────────────────────────────────────────

export function usePaidNotBilled(includeAbsorbed: boolean) {
  return useQuery({
    queryKey: paymentKeys.paidNotBilled(includeAbsorbed),
    queryFn: () => listPaidNotBilled({ includeAbsorbed }),
  });
}

export function useAddChargeToNextInvoice() {
  return usePaymentMutation((chargeId: number) => addChargeToNextInvoice(chargeId), { retry: false });
}

export function useAbsorbCharge() {
  return usePaymentMutation(
    ({ chargeId, request }: { chargeId: number; request: AbsorbRequest }) => absorbCharge(chargeId, request),
    { retry: false },
  );
}

// ─── Advance limits and My approvals (#297, demo only) ───────────────────────

export function useMyApprovals() {
  return useQuery({ queryKey: paymentKeys.myApprovals, queryFn: listMyApprovals });
}

export function useApprovalLimits(enabled = true) {
  return useQuery({ queryKey: paymentKeys.approvalLimits, queryFn: getApprovalLimits, enabled });
}

export function useSaveApprovalLimits() {
  return usePaymentMutation((body: ApprovalLimits) => putApprovalLimits(body), { retry: false });
}

export function useRejectApproval() {
  return usePaymentMutation(
    ({ paymentId, note }: { paymentId: number; note: string }) => rejectApproval(paymentId, note),
    { retry: false },
  );
}
